import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, CheckCircle, Loader2, ShieldCheck, Check, Circle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../../services/api';
import { 
    loginUser, 
    registerUser, 
    sendForgotPasswordOTP, 
    resendOTP,
    verifyOTP, 
    resetPassword,
    setNewPassword as setNewPasswordRequest
} from '../services/authService';
import SuccessToast from '../../../shared/components/ui/SuccessToast';
import { useCountdown } from '../hooks/useCountdown';
import {
    getApiErrorMessage,
    getRetryAfterSeconds,
    parseOtpSentResponse,
    parseVerifyOtpResponse,
} from '../utils/authResponse';
import edgeLogo from '../../../assets/images/edge_logo-removebg.png';
import './UnifiedAuth.css';

// Carousel Images
import loginImg1 from '../../../assets/images/1 8.png';
import loginImg2 from '../../../assets/images/2 5.png';
import loginImg3 from '../../../assets/images/3 5.png';

// A user who signs in with the emailed temporary password (password_set === false) is NOT logged in
// to the app yet. Their token is parked here (this tab only) just long enough to call set-password.
const PENDING_PASSWORD_KEY = 'pending_password_setup';
const readPendingPasswordSetup = () => {
    try {
        const raw = sessionStorage.getItem(PENDING_PASSWORD_KEY);
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed?.token ? parsed : null;
    } catch {
        return null;
    }
};
const clearPendingPasswordSetup = () => sessionStorage.removeItem(PENDING_PASSWORD_KEY);

const STEPS = {
    EMAIL: 'EMAIL',
    OTP: 'OTP',
    RESET: 'RESET',
    SUCCESS: 'SUCCESS'
};

const SLIDES = [
    {
        image: loginImg1,
        title: "Accelerate Your RFP Response Process",
        desc: "Transform complex RFP documents into structured workflows with AI-driven analysis and streamlined execution."
    },
    {
        image: loginImg2,
        title: "AI-Powered Requirement Intelligence",
        desc: "Automatically extract requirements, identify key evaluation criteria, and generate accurate, context-aware responses."
    },
    {
        image: loginImg3,
        title: "Efficient Collaboration, On-Time Delivery",
        desc: "Coordinate teams, manage workload, and deliver high-quality proposals with complete visibility and control."
    }
];

// Live checks shown while choosing a new password.
// NOTE: placeholder rules – replace with the backend's real password policy when it is known.
const MIN_PASSWORD_LENGTH = 8;
const getPasswordChecks = (pw) => ({
    length: pw.length >= MIN_PASSWORD_LENGTH,
    mixedCase: /[a-z]/.test(pw) && /[A-Z]/.test(pw),
    number: /\d/.test(pw),
    special: /[^A-Za-z0-9]/.test(pw),
});
const PASSWORD_RULES = [
    { key: 'length', label: `At least ${MIN_PASSWORD_LENGTH} characters` },
    { key: 'mixedCase', label: 'Upper and lower case letters' },
    { key: 'number', label: 'A number' },
    { key: 'special', label: 'A special character' },
];
const STRENGTH_LABELS = ['Too short', 'Weak', 'Fair', 'Good', 'Strong'];

const UnifiedAuth = ({ onLogin }) => {
    const [view, setView] = useState(() => {
        const path = window.location.pathname;
        if (path.includes('set-password')) return 'set-password';
        if (path.includes('signup')) return 'signup';
        if (path.includes('forgot-password')) return 'forgot-password';
        return 'login';
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [currentSlide, setCurrentSlide] = useState(0);
    const [rememberMe, setRememberMe] = useState(false);
    const [toast, setToast] = useState(null);
    const [authStatus, setAuthStatus] = useState('idle');
    const [isShaking, setIsShaking] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const reason = searchParams.get('reason');
    const redirectTo = searchParams.get('redirect');

    // Form Data
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [signupData, setSignupData] = useState({
        first_name: '',
        last_name: '',
        designation: '',
        phone_number: ''
    });

    // Forgot Password States
    const [fpStep, setFpStep] = useState(STEPS.EMAIL);
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const resendTimer = useCountdown(); // wait before another OTP can be requested
    const [resetToken, setResetToken] = useState('');

    // First-login "set new password" states (users created by an admin)
    const [tempPassword, setTempPassword] = useState('');
    const [newPasswordValue, setNewPasswordValue] = useState('');
    const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
    const [showTempPassword, setShowTempPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const showToastMsg = (message, type = 'success') => {
        setToast({ message, type });
    };

    // Carousel Timer
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Auto-close Toast Timer
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => {
                setToast(null);
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    useEffect(() => {
        const path = window.location.pathname;
        if (path.includes('set-password')) setView('set-password');
        else if (path.includes('signup')) setView('signup');
        else if (path.includes('forgot-password')) setView('forgot-password');
        else setView('login');

        setError('');
        setSuccessMessage('');

        // "Set new password" only makes sense right after logging in with the temporary password.
        if (path.includes('set-password')) {
            const pending = readPendingPasswordSetup();
            if (!pending) {
                navigate('/login', {
                    replace: true,
                    state: { message: 'Please log in with the temporary password from your email first.' }
                });
                return;
            }
            if (pending.email) setEmail(pending.email);
        }

        if (location.state?.message) {
            setSuccessMessage(location.state.message);
            showToastMsg(location.state.message, 'success');
        }

        if (reason === 'session-expired') {
            const expiredMsg = 'Your session has expired. Please login in again to continue.';
            setError(expiredMsg);
            showToastMsg(expiredMsg, 'error');
        } else if (reason === 'logout') {
            const logoutMsg = 'Logged out successfully.';
            showToastMsg(logoutMsg, 'success');
        }
    }, [location.pathname, location.state, reason]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setAuthStatus('loading');
        setError('');
        const startTime = Date.now();

        try {
            const response = await loginUser(email, password);
            const data = response.data;
            const token = data.access_token;
            const userData = data.user;

            // New user signing in with the temporary password: take them to "set new password".
            if (token && (data.password_set === false || userData?.password_set === false)) {
                localStorage.removeItem('access_token');
                localStorage.removeItem('user');
                sessionStorage.setItem(PENDING_PASSWORD_KEY, JSON.stringify({ token, email }));
                setPassword('');
                setAuthStatus('idle');
                navigate('/set-password', {
                    state: { email, message: 'You are a new user. Please set a new password to continue.' }
                });
                return;
            }

            if (token) {
                localStorage.setItem('access_token', token);
                localStorage.setItem('user', JSON.stringify(userData));

                if (localStorage.getItem('access_token') === token) {
                    setAuthStatus('success');
                    showToastMsg('Logged in successfully!', 'success');
                    
                    setTimeout(() => {
                        onLogin(true, userData);
                        if (redirectTo) navigate(redirectTo);
                        else navigate('/dashboard');
                    }, 1200);
                } else {
                    throw new Error('Failed to verify session storage.');
                }
            }
        } catch (err) {
            const errorMsg = err.response?.data?.detail
                ? (Array.isArray(err.response.data.detail) ? err.response.data.detail[0]?.msg : err.response.data.detail)
                : (err.response?.data?.message || err.message || 'Login failed.');
            
            // Respect a natural error loading duration (e.g. 1.5 seconds) so error doesn't flash abruptly
            const elapsed = Date.now() - startTime;
            const remainingDelay = Math.max(0, 1500 - elapsed);

            setTimeout(() => {
                setError(errorMsg);
                setAuthStatus('idle');
                
                // Trigger beautiful shake animation
                setIsShaking(true);
                setTimeout(() => setIsShaking(false), 500);
            }, remainingDelay);
        }
    };

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const response = await registerUser({ email, password, ...signupData });
            if (response.status === 200 || response.status === 201) {
                const successMsg = response.data?.message || response.data?.detail || 'Registration successful! Please login.';
                setSuccessMessage(successMsg);
                showToastMsg(successMsg, 'success');
                setView('login');
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            const errorMsg = Array.isArray(detail) ? detail[0]?.msg : (detail || err.response?.data?.message || 'Registration failed.');
            setError(errorMsg);
            showToastMsg(errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    // POST /rfp/auth/forgot-password  { email }  -> emails a 6-digit OTP (valid for 5 minutes).
    // The answer is the same whether or not the email is registered, so the screen never says which.
    const handleSendOTP = async (e) => {
        if (e) e.preventDefault();
        if (loading || resendTimer.secondsLeft > 0) return; // wait out the resend timer

        setLoading(true);
        setError('');
        try {
            const response = await sendForgotPasswordOTP(email.trim());
            const { message, resendAfter } = parseOtpSentResponse(response.data);
            resendTimer.start(resendAfter);
            showToastMsg(message, 'success');
            setFpStep(STEPS.OTP);
        } catch (err) {
            const errorMsg = err.response?.status === 422
                ? 'Please enter a valid email address.'
                : getApiErrorMessage(err, 'Failed to send OTP. Please try again.');
            setError(errorMsg);
            showToastMsg(errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };
    // POST /rfp/auth/resend-otp  { email }  -> a NEW code is emailed and the previous one stops working.
    // Asking again within 30 seconds is a 429 whose body has `retry_after` (seconds left).
    const handleResendOTP = async (e) => {
        if (e) e.preventDefault();
        if (loading || resendTimer.secondsLeft > 0) return; // wait out the resend timer

        setLoading(true);
        setError('');
        try {
            const response = await resendOTP(email.trim());
            const { message, resendAfter } = parseOtpSentResponse(response.data);

            // The old code no longer works: start with empty boxes, cursor in the first one.
            setOtp(['', '', '', '', '', '']);
            setTimeout(() => document.getElementById('otp-0')?.focus(), 0);

            resendTimer.start(resendAfter);
            showToastMsg(message, 'success');
        } catch (err) {
            if (err.response?.status === 429) {
                // Too soon: show the time that is really left and keep the current code.
                const secondsLeft = getRetryAfterSeconds(err);
                resendTimer.start(secondsLeft);
                const waitMsg = `Please wait ${secondsLeft} second${secondsLeft === 1 ? '' : 's'} before requesting a new code.`;
                setError(waitMsg);
                showToastMsg(waitMsg, 'error');
            } else {
                const errorMsg = err.response?.status === 422
                    ? 'Please enter a valid email address.'
                    : getApiErrorMessage(err, 'Could not resend the code. Please try again.');
                setError(errorMsg);
                showToastMsg(errorMsg, 'error');
            }
        } finally {
            setLoading(false);
        }
    };
    // POST /rfp/auth/verify-otp  { email, otp }  -> a reset token (valid 10 minutes) for the next step.
    // A wrong or expired code is a 400; 5 wrong tries cancel the code, so a new one must be requested.
    const handleVerifyOTP = async (e) => {
        e.preventDefault();
        if (loading) return;

        const fail = (message) => {
            setError(message);
            showToastMsg(message, 'error');
        };

        const code = otp.join('');
        if (!/^\d{6}$/.test(code)) {
            fail('Please enter the 6-digit code.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await verifyOTP(email.trim(), code);
            const { resetToken: token, message } = parseVerifyOtpResponse(response.data);

            if (!token) {
                fail('Could not verify the code. Please try again.');
                return;
            }

            setResetToken(token);
            showToastMsg(message, 'success');
            setFpStep(STEPS.RESET);
        } catch (err) {
            const status = err.response?.status;
            if (status === 422) {
                fail('Please enter a valid 6-digit code.');
            } else {
                fail(getApiErrorMessage(
                    err,
                    'The code is incorrect or has expired. After 5 wrong tries you will need a new code.'
                ));
            }
            if (status === 400) {
                // Start over with empty boxes, cursor in the first one.
                setOtp(['', '', '', '', '', '']);
                setTimeout(() => document.getElementById('otp-0')?.focus(), 0);
            }
        } finally {
            setLoading(false);
        }
    };
    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setError('Passwords do not match.');
            showToastMsg('Passwords do not match.', 'error');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await resetPassword(resetToken, newPassword, confirmPassword);
            const successMsg = response.data.message || response.data.detail || 'Password reset successful!';
            setSuccessMessage(successMsg);
            showToastMsg(successMsg, 'success');
            setFpStep(STEPS.SUCCESS);
        } catch (err) {
            const errorMsg = err.response?.data?.detail || err.response?.data?.message || 'Reset failed.';
            setError(errorMsg);
            showToastMsg(errorMsg, 'error');
        } finally {
            setLoading(false);
        }
    };

    // First login with the temporary password from the welcome email: choose a new password.
    const handleSetNewPassword = async (e) => {
        e.preventDefault();
        setError('');

        const fail = (message) => {
            setError(message);
            showToastMsg(message, 'error');
        };

        if (newPasswordValue.length < MIN_PASSWORD_LENGTH) {
            return fail(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        }
        if (newPasswordValue === tempPassword) {
            return fail('New password must be different from the temporary password.');
        }
        if (newPasswordValue !== confirmPasswordValue) {
            return fail('New password and confirm password do not match.');
        }

        const pending = readPendingPasswordSetup();
        if (!pending) {
            clearPendingPasswordSetup();
            navigate('/login', {
                replace: true,
                state: { message: 'Your temporary session has ended. Please log in again with the temporary password.' }
            });
            return;
        }

        setLoading(true);
        try {
            // POST /rfp/auth/set-password  { old_password, new_password, confirm_password }
            await setNewPasswordRequest({
                oldPassword: tempPassword,
                newPassword: newPasswordValue,
                confirmPassword: confirmPasswordValue,
                token: pending.token,
            });

            // Done: drop the parked token, clear the form and send the user to log in again.
            clearPendingPasswordSetup();
            setTempPassword('');
            setNewPasswordValue('');
            setConfirmPasswordValue('');
            setPassword('');
            navigate('/login', {
                replace: true,
                state: { message: 'Password updated successfully. Please log in with your new password.' }
            });
        } catch (err) {
            // 401: the temporary login token is no longer valid -> start over from login.
            if (err.response?.status === 401) {
                clearPendingPasswordSetup();
                navigate('/login', {
                    replace: true,
                    state: { message: 'Your temporary session expired. Please log in again with the temporary password.' }
                });
                return;
            }
            // 400: wrong old password / same as old / already set.  422: validation error.
            const detail = err.response?.data?.detail;
            fail(
                Array.isArray(detail)
                    ? (detail[0]?.msg || 'Could not set the new password.')
                    : (detail || err.response?.data?.message || err.message || 'Could not set the new password.')
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOtpChange = (index, value) => {
        if (isNaN(value)) return;
        const newOtp = [...otp];
        newOtp[index] = value.substring(value.length - 1);
        setOtp(newOtp);
        if (value && index < 5) document.getElementById(`otp-${index + 1}`)?.focus();
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) document.getElementById(`otp-${index - 1}`)?.focus();
    };

    const renderHeader = (title, subtitle) => (
        <div className="auth-header-v5">
            <div className="auth-logo-v5">
                <img src={edgeLogo} className="edge-auth-logo" alt="EDGE Logo" />
            </div>
            <h1>{title}</h1>
            <p>{subtitle}</p>
        </div>
    );

    const renderField = (label, type, placeholder, value, onChange, name) => {
        const isFieldDisabled = loading || (view === 'login' && authStatus !== 'idle');
        const isErrorField = !!error && view === 'login' && (label === 'Email' || label === 'Password');

        return (
            <div className="auth-field-v5">
                <label>{label}</label>
                <input
                    type={type}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    name={name}
                    required
                    disabled={isFieldDisabled}
                    style={{
                        borderColor: isErrorField ? '#EF4444' : '',
                        boxShadow: isErrorField ? '0 0 0 3px rgba(239, 68, 68, 0.08)' : '',
                        transition: 'all 0.25s ease'
                    }}
                />
            </div>
        );
    };

    const renderPasswordField = (label, placeholder, value, onChange, visible, onToggle, autoComplete) => (
        <div className="auth-field-v5">
            <label>{label}</label>
            <div className="auth-password-v5">
                <input
                    type={visible ? 'text' : 'password'}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    autoComplete={autoComplete}
                    required
                    disabled={loading}
                />
                <button type="button" className="eye-btn-v5" onClick={onToggle} disabled={loading} aria-label={visible ? `Hide ${label}` : `Show ${label}`}>
                    {visible ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
            </div>
        </div>
    );

    const passwordChecks = getPasswordChecks(newPasswordValue);
    const passwordScore = newPasswordValue
        ? (passwordChecks.length ? Object.values(passwordChecks).filter(Boolean).length : 0)
        : 0;
    const passwordsMismatch = confirmPasswordValue.length > 0 && confirmPasswordValue !== newPasswordValue;

    return (
        <div className="auth-container-v5">
            {/* Left: Carousel */}
            <div className="auth-left-v5">
                {SLIDES.map((slide, index) => (
                    <div
                        key={index}
                        className={`auth-slide-v5 ${index === currentSlide ? 'active' : ''}`}
                    >
                        <img src={slide.image} alt={slide.title} />
                        <div className="slide-overlay-v5">
                            <div className="slide-text-v5">
                                <h2>{slide.title}</h2>
                                <p>{slide.desc}</p>
                                <div className="slide-dots-v5">
                                    {SLIDES.map((_, i) => (
                                        <div
                                            key={i}
                                            className={`dot-v5 ${i === currentSlide ? 'active' : ''}`}
                                            onClick={() => setCurrentSlide(i)}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
                <div className="exalogic-logo-v5">
                    <img src={edgeLogo} alt="EDGE" className="edge-panel-logo" />
                </div>
            </div>

            {/* Right: Form Area */}
            <div className="auth-right-v5">
                <div className="auth-content-wrapper-v5">
                    <AnimatePresence mode="wait">
                        {view === 'login' && (
                            <motion.div 
                                key="login" 
                                initial={{ opacity: 0, y: 10 }} 
                                animate={isShaking ? { x: [-8, 8, -6, 6, -4, 4, 0], y: 0 } : { opacity: 1, y: 0 }} 
                                exit={{ opacity: 0, y: -10 }} 
                                transition={{ duration: 0.4 }}
                                className="view-wrapper-v5"
                            >
                                {renderHeader("Welcome Back", "Please Login To Your Account")}
                                
                                <AnimatePresence>
                                    {successMessage && (
                                        <motion.div 
                                            initial={{ opacity: 0, height: 0 }} 
                                            animate={{ opacity: 1, height: 'auto' }} 
                                            exit={{ opacity: 0, height: 0 }} 
                                            transition={{ duration: 0.2 }}
                                            className="auth-msg-v5 success"
                                            style={{ overflow: 'hidden', width: '100%' }}
                                        >
                                            {successMessage}
                                        </motion.div>
                                    )}
                                    {error && (
                                        <motion.div 
                                            initial={{ opacity: 0, height: 0 }} 
                                            animate={{ opacity: 1, height: 'auto' }} 
                                            exit={{ opacity: 0, height: 0 }} 
                                            transition={{ duration: 0.2 }}
                                            className="auth-msg-v5 error"
                                            style={{ overflow: 'hidden', width: '100%' }}
                                        >
                                            {error}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <form 
                                    className="auth-form-v5" 
                                    onSubmit={handleLogin}
                                    style={{
                                        opacity: authStatus !== 'idle' ? 0.45 : 1,
                                        filter: authStatus !== 'idle' ? 'blur(3px)' : 'none',
                                        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                                        pointerEvents: authStatus !== 'idle' ? 'none' : 'auto'
                                    }}
                                >
                                    {renderField("Email", "email", "Enter your email", email, (e) => setEmail(e.target.value))}
                                    <div className="auth-field-v5">
                                        <label>Password</label>
                                        <div className="auth-password-v5">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter your password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                disabled={authStatus !== 'idle'}
                                                style={{
                                                    borderColor: error ? '#EF4444' : '',
                                                    boxShadow: error ? '0 0 0 3px rgba(239, 68, 68, 0.08)' : '',
                                                    transition: 'all 0.25s ease'
                                                }}
                                            />
                                            <button type="button" className="eye-btn-v5" onClick={() => setShowPassword(!showPassword)} disabled={authStatus !== 'idle'}>
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="auth-options-v5">
                                        <label className="checkbox-v5">
                                            <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={authStatus !== 'idle'} />
                                            <span>Remember me</span>
                                        </label>
                                        <button type="button" className="link-btn-v5" onClick={() => navigate('/forgot-password')} disabled={authStatus !== 'idle'}>Forgot Password</button>
                                    </div>
                                    <button 
                                        type="submit" 
                                        className="auth-btn-v5" 
                                        disabled={authStatus !== 'idle'}
                                        style={{
                                            background: authStatus === 'success' ? '#10B981' : authStatus === 'loading' ? '#E9471D' : '',
                                            transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                                        }}
                                    >
                                        {authStatus === 'loading' && (
                                            <>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                                    className="btn-spinner-icon"
                                                >
                                                    <Loader2 size={16} />
                                                </motion.div>
                                                <span>Signing you in...</span>
                                            </>
                                        )}
                                        {authStatus === 'success' && (
                                            <>
                                                <motion.div
                                                    initial={{ scale: 0 }}
                                                    animate={{ scale: 1 }}
                                                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                                                    className="btn-success-icon"
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                                </motion.div>
                                                <span>Success!</span>
                                            </>
                                        )}
                                        {authStatus === 'idle' && <span>Login</span>}
                                        {authStatus === 'error' && <span>Login</span>}
                                    </button>
                                </form>
                                <div className="auth-footer-v5">
                                    <p>Don't have an account? <button className="link-btn-v5 bold" onClick={() => navigate('/signup')} disabled={authStatus !== 'idle'}>Sign Up</button></p>
                                </div>
                            </motion.div>
                        )}

                        {view === 'set-password' && (
                            <motion.div key="set-password" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="view-wrapper-v5">
                                {renderHeader("Set Your New Password", "Welcome to EDGE RFP")}

                                <div className="auth-notice-v5" role="note">
                                    <ShieldCheck size={20} className="auth-notice-icon-v5" />
                                    <div>
                                        <strong>You are a new user</strong>
                                        <span>Please reset your password. Enter the temporary password from your welcome email, then choose a new one.</span>
                                    </div>
                                </div>

                                {error && <div className="auth-msg-v5 error" role="alert">{error}</div>}

                                <form className="auth-form-v5" onSubmit={handleSetNewPassword}>
                                    <div className="auth-field-v5">
                                        <label>Email</label>
                                        <input type="email" value={email} readOnly disabled aria-readonly="true" />
                                    </div>

                                    {renderPasswordField(
                                        "Temporary Password", "Enter the temporary password from your email",
                                        tempPassword, (e) => setTempPassword(e.target.value),
                                        showTempPassword, () => setShowTempPassword(!showTempPassword), 'current-password'
                                    )}

                                    {renderPasswordField(
                                        "New Password", "Create a new password",
                                        newPasswordValue, (e) => setNewPasswordValue(e.target.value),
                                        showNewPassword, () => setShowNewPassword(!showNewPassword), 'new-password'
                                    )}

                                    {newPasswordValue && (
                                        <div className="pw-helper-v5">
                                            <div className="pw-meter-v5" aria-hidden="true">
                                                {[1, 2, 3, 4].map((segment) => (
                                                    <span
                                                        key={segment}
                                                        className={`pw-meter-seg-v5 ${segment <= passwordScore ? `level-${passwordScore}` : ''}`}
                                                    />
                                                ))}
                                            </div>
                                            <span className={`pw-meter-label-v5 level-${passwordScore}`}>{STRENGTH_LABELS[passwordScore]}</span>
                                        </div>
                                    )}

                                    <ul className="pw-rules-v5" aria-label="Password requirements">
                                        {PASSWORD_RULES.map((rule) => {
                                            const ok = passwordChecks[rule.key];
                                            return (
                                                <li key={rule.key} className={ok ? 'ok' : ''}>
                                                    {ok ? <Check size={14} /> : <Circle size={10} />}
                                                    <span>{rule.label}</span>
                                                </li>
                                            );
                                        })}
                                    </ul>

                                    {renderPasswordField(
                                        "Confirm Password", "Re-enter the new password",
                                        confirmPasswordValue, (e) => setConfirmPasswordValue(e.target.value),
                                        showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), 'new-password'
                                    )}
                                    {passwordsMismatch && (
                                        <div className="pw-mismatch-v5" role="alert">Passwords do not match.</div>
                                    )}

                                    <button type="submit" className="auth-btn-v5" disabled={loading || passwordsMismatch}>
                                        {loading ? <Loader2 className="spin-v5" size={20} /> : "Save Password"}
                                    </button>
                                </form>

                                <div className="auth-footer-v5">
                                    <button className="link-btn-v5" onClick={() => navigate('/login')} disabled={loading}>
                                        <ArrowLeft size={16} /> Back to Login
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {view === 'signup' && (
                            <motion.div key="signup" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="view-wrapper-v5">
                                {renderHeader("Create Account", "Join EDGE RFP today")}
                                {error && <div className="auth-msg-v5 error">{error}</div>}
                                <form className="auth-form-v5" onSubmit={handleSignup}>
                                    <div className="auth-row-v5">
                                        {renderField("First Name", "text", "First name", signupData.first_name, (e) => setSignupData({ ...signupData, first_name: e.target.value }))}
                                        {renderField("Last Name", "text", "Last name", signupData.last_name, (e) => setSignupData({ ...signupData, last_name: e.target.value }))}
                                    </div>
                                    {renderField("Email", "email", "Enter your email", email, (e) => setEmail(e.target.value))}
                                    {renderField("Designation", "text", "Enter designation", signupData.designation, (e) => setSignupData({ ...signupData, designation: e.target.value }))}
                                    {renderField("Phone", "tel", "Enter phone number", signupData.phone_number, (e) => setSignupData({ ...signupData, phone_number: e.target.value }))}
                                    <div className="auth-field-v5">
                                        <label>Password</label>
                                        <div className="auth-password-v5">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Create a password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                            />
                                            <button type="button" className="eye-btn-v5" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                            </button>
                                        </div>
                                    </div>
                                    <button type="submit" className="auth-btn-v5" disabled={loading}>
                                        {loading ? <Loader2 className="spin-v5" size={20} /> : "Sign Up"}
                                    </button>
                                </form>
                                <div className="auth-footer-v5">
                                    <p>Already have an account? <button className="link-btn-v5 bold" onClick={() => navigate('/login')}>Login</button></p>
                                </div>
                            </motion.div>
                        )}

                        {view === 'forgot-password' && (
                            <motion.div key="forgot" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="view-wrapper-v5">
                                {fpStep === STEPS.EMAIL && (
                                    <>
                                        {renderHeader("Forgot Password?", "Enter email to receive an OTP")}
                                        {error && <div className="auth-msg-v5 error">{error}</div>}
                                        <form className="auth-form-v5" onSubmit={handleSendOTP}>
                                            {renderField("Email", "email", "Enter your email", email, (e) => setEmail(e.target.value))}
                                            <button type="submit" className="auth-btn-v5" disabled={loading || resendTimer.secondsLeft > 0}>
                                                {loading
                                                    ? <Loader2 className="spin-v5" size={20} />
                                                    : (resendTimer.secondsLeft > 0 ? `Send OTP again in ${resendTimer.secondsLeft}s` : "Send OTP")}
                                            </button>
                                        </form>
                                    </>
                                )}
                                {fpStep === STEPS.OTP && (
                                    <>
                                        {renderHeader("Verify OTP", `Code sent to ${email}`)}
                                        {error && <div className="auth-msg-v5 error">{error}</div>}
                                        <form className="auth-form-v5" onSubmit={handleVerifyOTP}>
                                            <div className="auth-otp-v5">
                                                {otp.map((digit, i) => (
                                                    <input key={i} id={`otp-${i}`} type="text" maxLength="1" value={digit} onChange={(e) => handleOtpChange(i, e.target.value)} onKeyDown={(e) => handleOtpKeyDown(i, e)} />
                                                ))}
                                            </div>
                                            <button type="submit" className="auth-btn-v5" disabled={loading || otp.join('').length < 6}>Verify OTP</button>
                                            <button
                                                type="button"
                                                className="link-btn-v5"
                                                onClick={handleResendOTP}
                                                disabled={loading || resendTimer.secondsLeft > 0}
                                                style={{ marginTop: '1rem', width: '100%' }}
                                            >
                                                {resendTimer.secondsLeft > 0 ? `Resend OTP in ${resendTimer.secondsLeft}s` : 'Resend OTP'}
                                            </button>
                                        </form>
                                    </>
                                )}
                                {fpStep === STEPS.RESET && (
                                    <>
                                        {renderHeader("Reset Password", "Set a new password")}
                                        {error && <div className="auth-msg-v5 error">{error}</div>}
                                        <form className="auth-form-v5" onSubmit={handleResetPassword}>
                                            {renderField("New Password", "password", "Enter new password", newPassword, (e) => setNewPassword(e.target.value))}
                                            {renderField("Confirm Password", "password", "Confirm new password", confirmPassword, (e) => setConfirmPassword(e.target.value))}
                                            <button type="submit" className="auth-btn-v5" disabled={loading}>Reset Password</button>
                                        </form>
                                    </>
                                )}
                                {fpStep === STEPS.SUCCESS && (
                                    <div className="auth-success-v5">
                                        <CheckCircle size={64} color="#22C55E" />
                                        <h2>Success!</h2>
                                        <p>{successMessage}</p>
                                        <button className="auth-btn-v5" onClick={() => { navigate('/login'); setFpStep(STEPS.EMAIL); }}>Back to Login</button>
                                    </div>
                                )}
                                {fpStep !== STEPS.SUCCESS && (
                                    <div className="auth-footer-v5" style={{ marginTop: '2rem', borderTop: '1px solid #F1F5F9', paddingTop: '1.5rem' }}>
                                        <button className="link-btn-v5" onClick={() => { navigate('/login'); setFpStep(STEPS.EMAIL); }}>
                                            <ArrowLeft size={16} /> Back to Login
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                
                <AnimatePresence>
                    {(authStatus === 'loading' || authStatus === 'success') && (
                        <motion.div
                            className="auth-premium-overlay"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                        >
                            <motion.div
                                className={`auth-premium-card ${authStatus}`}
                                initial={{ scale: 0.96, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.96, opacity: 0 }}
                                transition={{ type: 'spring', damping: 25, stiffness: 350 }}
                            >
                                <div className="auth-card-icon-wrap">
                                    {authStatus === 'loading' ? (
                                        <motion.div
                                            animate={{ rotate: 360 }}
                                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                            className="auth-spinner-stroke"
                                        />
                                    ) : (
                                        <motion.div
                                            initial={{ scale: 0, rotate: -45 }}
                                            animate={{ scale: 1, rotate: 0 }}
                                            transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                                            className="auth-success-check-wrap"
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                        </motion.div>
                                    )}
                                </div>
                                <h3>
                                    {authStatus === 'loading' ? "Signing you in" : "Login successful"}
                                </h3>
                                <p>
                                    {authStatus === 'loading' 
                                        ? "Verifying credentials and preparing workspace..." 
                                        : "Redirecting to your workspace..."}
                                </p>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <div className="auth-demo-footer">
                    <p>Beta Version - In Demo Mode Copyright (C) of Exalogic Consulting - 2026</p>
                </div>
            </div>

            {loading && view !== 'login' && (
                <div className="auth-freeze-overlay">
                    <div className="auth-freeze-loader-card">
                        <Loader2 className="spin-v5" size={40} color="#FF5526" />
                        <h3>{view === 'signup' ? 'Creating your account...' : view === 'set-password' ? 'Saving your new password...' : 'Please wait...'}</h3>
                        <p>{view === 'signup' ? 'We are setting up your account.' : view === 'set-password' ? 'Please wait while we update your password securely.' : 'We are processing your request.'}</p>
                    </div>
                </div>
            )}

            <SuccessToast toast={toast} />
        </div>
    );
};

export default UnifiedAuth;
